/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import _ from 'lodash';

import BottomBar from '../BottomBar';
import MultiSelectCheckbox from '../form/MultiSelectCheckbox';
import usePersistedDraft from '../hooks/usePersistedDraft';
import { updatePracticePreferences } from '../../../actions/preferences';
import { toMultiSelectOptions } from '../helpers';

const PracticeDetails4 = ({
  openLoadingPanel,
  closeLoadingPanel,
  previousPage,
  nextPage,
  errorCallback,
  currentSubStep,
  draftValues,
  onDraftChange,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Pull options from Redux store
  const approachesToCareOptions = useSelector(state =>
    toMultiSelectOptions(_.get(state.misc, 'approachesToCare', [])),
  );
  const therapeuticOrientationOptions = useSelector(state =>
    toMultiSelectOptions(_.get(state.misc, 'therapeuticOrientations', [])),
  );

  const {
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: draftValues || {
      approach_to_cares: [],
      therapeutic_orientations: [],
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const onSubmit = async values => {
    const params = {
      approach_to_cares: _.get(values, 'approach_to_cares', []),
      therapeutic_orientations: _.get(values, 'therapeutic_orientations', []),
    };

    const errorMessage = intl.formatMessage({
      defaultMessage: 'There was a problem updating your practice details.',
    });

    openLoadingPanel(
      intl.formatMessage({ defaultMessage: 'Updating practice details...' }),
    );

    try {
      await dispatch(updatePracticePreferences(params));
      closeLoadingPanel();
      nextPage();
    } catch (err) {
      errorCallback(err, errorMessage);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title">
        <h1>{intl.formatMessage({ defaultMessage: 'Practice Details' })}</h1>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <div className="sub-page-indicator">{`${currentSubStep + 1}/7`}</div>
      </div>

      <h3>
        {intl.formatMessage({
          defaultMessage: 'What is your approach to care?',
        })}
      </h3>
      <h4>
        {intl.formatMessage({
          defaultMessage:
            'Please choose the top three that most apply to your practice.',
        })}
      </h4>
      <div className="input-row">
        <Controller
          name="approach_to_cares"
          control={control}
          rules={{
            validate: value =>
              value && value.length > 0
                ? true
                : intl.formatMessage({
                    defaultMessage: 'You must select 1 approach',
                  }),
          }}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              options={approachesToCareOptions}
              error={errors.approach_to_cares?.message}
            />
          )}
        />
      </div>

      <h3>
        {intl.formatMessage({
          defaultMessage:
            'Please select the therapeutic orientations you use in working with your clients.',
        })}
      </h3>
      <div className="input-row">
        <Controller
          name="therapeutic_orientations"
          control={control}
          render={({ field }) => (
            <MultiSelectCheckbox
              {...field}
              options={therapeuticOrientationOptions}
            />
          )}
        />
      </div>

      <BottomBar
        openLoadingPanel={openLoadingPanel}
        closeLoadingPanel={closeLoadingPanel}
        nextPage={nextPage}
        previousPage={previousPage}
        errorCallback={errorCallback}
        currentSubStep={currentSubStep}
      />
    </form>
  );
};

PracticeDetails4.propTypes = {
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  previousPage: PropTypes.func.isRequired,
  errorCallback: PropTypes.func.isRequired,
  currentSubStep: PropTypes.number.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
};

export default PracticeDetails4;
